import React, { useState, useEffect, useRef } from 'react';
import { Building, CheckCircle, Calendar, Users } from 'lucide-react';
import axios from 'axios';

const StatsSection = () => {
  const [stats, setStats] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';

  const iconMap = {
    building: Building,
    'check-circle': CheckCircle,
    calendar: Calendar,
    users: Users
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/content/stats`);
        setStats(response.data);
        setCounters(response.data.map(() => 0));
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [backendUrl]);

  useEffect(() => {
    if (loading || stats.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          animateCounters();
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible, loading, stats.length]);

  const animateCounters = () => {
    stats.forEach((stat, index) => {
      const targetValue = parseInt(stat.value.replace(/\D/g, ''));
      const suffix = stat.value.replace(/\d/g, '');
      let current = 0;
      const increment = targetValue / 60;

      const timer = setInterval(() => {
        current += increment;
        if (current >= targetValue) {
          current = targetValue;
          clearInterval(timer);
        }

        setCounters(prev => {
          const newCounters = [...prev];
          newCounters[index] = Math.floor(current) + suffix;
          return newCounters;
        });
      }, 16);
    });
  };

  if (loading || stats.length === 0) return null;

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-black to-gray-900 border-y border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-silver mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Our track record speaks for itself. We've delivered excellence across hundreds of projects.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => {
            const IconComponent = iconMap[stat.icon];
            return (
              <div
                key={index}
                className={`text-center transform transition-all duration-700 delay-${index * 200} ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-100'
                  }`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-br from-silver to-gray-200 rounded-full">
                  <IconComponent size={32} className="text-white" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-silver mb-2 font-mono">
                  {isVisible ? counters[index] : '0+'}
                </div>
                <div className="text-gray-400 font-medium tracking-wide">
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Achievement Highlights */}
        <div className="mt-20 grid md:grid-cols-3 gap-8">
          <div className="glass-morphism rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-silver mb-2">99.9%</div>
            <div className="text-gray-400">Uptime Guarantee</div>
          </div>
          <div className="glass-morphism rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-silver mb-2">24/7</div>
            <div className="text-gray-400">Support Available</div>
          </div>
          <div className="glass-morphism rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-silver mb-2">ISO 27001</div>
            <div className="text-gray-400">Certified Security</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;